import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useUserContext } from './UserContext';
import * as brazeService from '../services/brazeService';

type WrappedFunctions = typeof brazeService;

type BrazeProviderProps = {
  children: ReactNode;
};

type BrazeContext = {
  isBrazeInitialized: boolean;
  setIsBrazeInitialized: (t: boolean) => void;
};

const BrazeContext = createContext<BrazeContext>({
  isBrazeInitialized: false,
  setIsBrazeInitialized: () => null,
});

const BrazeProvider = ({ children }: BrazeProviderProps) => {
  const [isBrazeInitialized, setIsBrazeInitialized] = useState<boolean>(false);

  const value = useMemo(
    () => ({
      isBrazeInitialized,
      setIsBrazeInitialized,
    }),
    [isBrazeInitialized],
  );

  return (
    <BrazeContext.Provider value={value}>{children}</BrazeContext.Provider>
  );
};

/**
 * Exposes Braze initialization state and any function exported from `brazeService`.
 * Wrapped functions only invoke the underlying service if the user has consented to Braze.
 */
const useBraze = () => {
  const {
    consentOptions: { allowBraze },
    isAuthenticated,
  } = useUserContext();
  const { isBrazeInitialized, setIsBrazeInitialized } =
    useContext(BrazeContext);

  const enableBraze = isAuthenticated && allowBraze;

  const functions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(brazeService).map(([key, method]) => [
          key,
          (...args: Parameters<typeof method>) => {
            if (enableBraze) (method as (...args: unknown[]) => void)(...args);
          },
        ]),
      ),
    [enableBraze],
    /**
     * We assert with `as` because Object.entries loses specific key types with Typescript
     */
  ) as WrappedFunctions;

  return useMemo(
    () => ({ isBrazeInitialized, setIsBrazeInitialized, ...functions }),
    [isBrazeInitialized, setIsBrazeInitialized, functions],
  );
};

export { BrazeContext, BrazeProvider, useBraze };
